import log from 'loglevel';

import {
  PUSH_NOTIFICATION_EVENTS,
  SERVICE_WORKER_MESSAGE_TYPES,
} from './hooks/usePushNotifications';

let registrationPromise: Promise<ServiceWorkerRegistration> | null = null;

function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker
    .register('/service-worker.js')
    .then((registration) => {
      log.info('Service Worker registered successfully');

      navigator.serviceWorker.addEventListener('message', (event) => {
        if (
          event.data?.type ===
          SERVICE_WORKER_MESSAGE_TYPES.PUSH_SUBSCRIPTION_CHANGED
        ) {
          window.dispatchEvent(
            new CustomEvent(PUSH_NOTIFICATION_EVENTS.PUSH_SUBSCRIPTION_CHANGE, {
              detail: event.data.subscription,
            }),
          );
        }
      });

      return registration;
    })
    .catch((error) => {
      log.error('Service Worker registration failed:', error);
      throw error;
    });
}

export function register(): Promise<ServiceWorkerRegistration> | null {
  if (!('serviceWorker' in navigator)) {
    log.warn('Service workers are not supported in this browser');
    return null;
  }

  if (registrationPromise) {
    return registrationPromise;
  }

  if (document.readyState === 'complete') {
    registrationPromise = registerServiceWorker();
  } else {
    registrationPromise = new Promise((resolve, reject) => {
      window.addEventListener('load', () => {
        registerServiceWorker().then(resolve, reject);
      });
    });
  }

  return registrationPromise;
}

export function getRegistration(): Promise<ServiceWorkerRegistration> | null {
  return registrationPromise;
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.unregister();
    });
  }
}

export async function deactivateInDev(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
    log.info('Service Worker registrations cleared in dev');
  } catch (error) {
    log.error('Failed to unregister service workers in dev:', error);
  }

  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
    log.info('Service Worker caches cleared in dev');
  } catch (error) {
    log.error('Failed to clear caches in dev:', error);
  }
}
